import React, { useEffect, useRef, useState } from 'react';
import { Grid, GridColumn, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { Box, MenuItem, Select, FormControl, styled, Button, InputLabel, SelectChangeEvent } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store';
import { getRateTypes, getTillageApiData } from '../../slices/productcostseeds.slice';
import { EditActionCell } from '../../components/DataTable/customcell/EditActionCell';
import { TillageTableData } from '../Modals/action-bill-modal/types';
import EditModalTillage from './EditTillageWorkModal';
import SearchBox from './SearchBox';
import { Add, Launch } from '@mui/icons-material';
import { ExcelExport } from '@progress/kendo-react-excel-export';

const TillageTabComponent: React.FC = () => {
  const StyledGrid = styled(Grid)`
  .k-column-title {
    min-width: 0;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    font-weight: bold; /* Make the font bold */
    font-family: "Roboto";
  }
  ,
  th {
    border-inline-start-width: 0px !important;
  }
  th > .k-cell-inner > .k-link {
    border-right: 2px solid var(--kendo-color-border);
    justify-content: center;
  }
  ,
  th > .k-cell-inner,
  .k-grid-header-wrap {
    background-color: white;
    height: 55px;
  }
  ,
  .k-table-row,
  .k-table-alt-row,
  .k-grid-table-wrap {
    background-color: #f3f4f6 !important;
  }
  ,
  .k-table-td {
    border-bottom: 1px solid var(--kendo-color-border);
    border-left: none;
    border-right: none;
    font-family: "Roboto";
    text-align: center;
  }
`;

 const gridColumns: any[] = [
    {
      field: "rateType",
      title: "Rate Type",
      width: "180px",
    },
   {
     field: "workName",
     title: "Work Name",
     width: "180px",
   },
   {
     field: "machineName",
     title: "Machine Name",
     width: "200px",
   },
   {
     field: "formattedPeriods",
     title: "Price Periods",
     width: "250px",
   },
   {
     title: "Actions",
     width: "100px",
     cells: {
       data: (props: any) => (
         <EditActionCell {...props} onEditClick={handleEditClick} />
       ),
     },
   },
 ];

 interface GridDataItem {
   id: number;
   workName: string;
   rateType: string;
   periods: Period[];
 }
 const [page, setPage] = useState({ skip: 0, take: 20 });
 const [isCreate, setIsCreate] = useState(false);
 const [showEditModal, setShowEditModal] = useState<boolean>(false);
 const [searchQuery, setSearchQuery] = useState<string>("");
 const [editItem, setEditItem] = useState<any>(null);
 const excelExportRef = useRef<ExcelExport | null>(null);
 const [rateValue, setRateValue] = useState<string>("All");
 const [data, setData] = useState<GridDataItem[]>([]);
 const { tillageOperationsData, rateTypesArray } = useAppSelector(
   (state: RootState) => state.seedsdropdown
 );

 const handlePageChange = (event: GridPageChangeEvent) => {
   setPage(event.page);
 };

 const dispatch = useAppDispatch();

 const createEmptyTillageItem = () => ({
   id: 0,
   rateType: "",
   workName: "",
   machineName: "",
   periods: [],
 });

 const handleSearchChange = (value: string) => {
   setSearchQuery(value);
 };

 const handleExport = () => {
   if (excelExportRef.current) {
     excelExportRef.current.save();
   }
 };

 const handleCloseEditModal = () => {
   setShowEditModal(false);
   dispatch(getTillageApiData());
   setEditItem(null);
   setIsCreate(false);
 };
 interface Period {
   costPerAcre: number;
   firstTilled: string;
   lastTilled: string;
   notes: string;
 }

 const handleFilterChange = (event: SelectChangeEvent<string>) => {
   const selectedFilter = event.target.value as string;
   setRateValue(selectedFilter);
 };

 useEffect(() => {
   setPage((prev) => ({ ...prev, skip: 0 }));
 }, [searchQuery, rateValue]);

 useEffect(() => {
   let filteredData = tillageOperationsData;
   if (rateValue !== "All") {
     filteredData = filteredData.filter((item) => item.rateType === rateValue);
   }
   if (searchQuery) {
     filteredData = filteredData.filter(
       (item) =>
         item.workName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         item.machineName?.toLowerCase().includes(searchQuery.toLowerCase())
     );
   }
   setData(filteredData);
 }, [rateValue, searchQuery, tillageOperationsData]);

 useEffect(() => {
   dispatch(getTillageApiData());
   dispatch(getRateTypes());
 }, [dispatch]);

 const handleEditClick = (item: TillageTableData) => {
   setEditItem(item);
   setShowEditModal(true);
 };

 const onAddTillagePriceClick = () => {
   setIsCreate(true);
   setEditItem(createEmptyTillageItem());
   setShowEditModal(true);
 };

 return (
   <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
     <Box display="flex" alignItems="center" gap={2}>
       <FormControl
         variant="outlined"
         size="small"
         style={{ minWidth: "200px" }}
       >
         <InputLabel>Rate Type</InputLabel>
         <Select
           value={rateValue}
           onChange={handleFilterChange}
           label="Rate Type"
         >
           <MenuItem value="All">All</MenuItem>
           {rateTypesArray.map((rateObj) => (
             <MenuItem key={rateObj.id} value={rateObj.name}>
               {rateObj.name}
             </MenuItem>
           ))}
         </Select>
       </FormControl>
       <SearchBox
         placeholder={"Search Work/Machine"}
         searchQuery={searchQuery}
         handleSearchChange={handleSearchChange}
       />
       <Button variant="contained" startIcon={<Add />} onClick={onAddTillagePriceClick}>
         Add Tillage Work
       </Button>
       <Button
         variant="contained"
         startIcon={<Launch />}
         onClick={handleExport}
       >
         Export
       </Button>
     </Box>
     <EditModalTillage
       isOpen={showEditModal}
       editItem={editItem}
       handleClose={handleCloseEditModal}
       isCreate={isCreate}
     />
     <ExcelExport data={data} ref={excelExportRef}>
       <StyledGrid
         data={data}
         dataItemKey="id"
         scrollable="scrollable"
         autoProcessData={true}
         pageable={{ buttonCount: 5, pageSizes: [10, 20, 30], info: true }}
         total={data.length}
         skip={page.skip}
         take={page.take}
         onPageChange={handlePageChange}
       >
         {gridColumns.map((columnProps: any) => (
           <GridColumn
             key={columnProps.field || columnProps.title}
             {...columnProps}
             title={columnProps.title}
           />
         ))}
       </StyledGrid>
     </ExcelExport>
   </Box>
 );
};

export default TillageTabComponent;

